// Subscribes email-capture forms to Klaviyo lists via the client-side
// Subscriptions API. Forms opt in with `data-klaviyo-subscribe` and are
// configured through data attributes rendered from theme settings.
//
//   data-klaviyo-subscribe            marks a form for handling
//   data-klaviyo-list="<listId>"      target Klaviyo list (required)
//   data-klaviyo-source="<label>"     value stored as the profile source
//   data-klaviyo-mode="replace"       (default) intercept + show inline success
//   data-klaviyo-mode="augment"       send to Klaviyo, then submit to Shopify
//   data-klaviyo-success="<text>"     success message for replace mode
//   [data-klaviyo-property="<key>"]   extra inputs sent as profile properties
//
// The public API key is read from window.FaithMovesKlaviyo.publicKey. When the
// key or list id is missing, forms fall back to their native Shopify behavior.

const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const REVISION = '2024-10-15';
const AUGMENT_TIMEOUT = 2500;

const config = window.FaithMovesKlaviyo || {};

function getEmail(form) {
  const input = form.querySelector('input[type="email"], input[name="contact[email]"]');
  return input ? input.value.trim() : '';
}

function buildPayload(form, email) {
  const attributes = { email };

  const nameInput = form.querySelector('input[name="contact[name]"]');
  if (nameInput && nameInput.value.trim()) {
    attributes.first_name = nameInput.value.trim();
  }

  const properties = {};
  form.querySelectorAll('[data-klaviyo-property]').forEach((el) => {
    const key = el.dataset.klaviyoProperty;
    if (key && el.value) properties[key] = el.value;
  });
  if (Object.keys(properties).length) attributes.properties = properties;

  return {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: form.dataset.klaviyoSource || 'Website',
        profile: { data: { type: 'profile', attributes } },
      },
      relationships: {
        list: { data: { type: 'list', id: form.dataset.klaviyoList } },
      },
    },
  };
}

async function subscribe(form, email) {
  const url = `${ENDPOINT}?company_id=${encodeURIComponent(config.publicKey)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', revision: REVISION },
    body: JSON.stringify(buildPayload(form, email)),
  });
  if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
}

function showMessage(form, text, isError) {
  let message = form.parentElement.querySelector('.klaviyo-message');
  if (!message) {
    message = document.createElement('p');
    message.className = 'klaviyo-message';
    message.setAttribute('role', 'status');
    form.insertAdjacentElement('afterend', message);
  }
  message.dataset.state = isError ? 'error' : 'success';
  message.textContent = text;
}

function handleReplace(form, event) {
  const email = getEmail(form);
  if (!email) return; // empty -> native "required" validation blocks submit, no POST fires
  // Always stop the native Shopify submission for newsletter/popup forms so they can
  // never hit Shopify's "I'm not a robot" challenge page, even if config is missing.
  event.preventDefault();

  const button = form.querySelector('button[type="submit"], button:not([type])');
  if (button) button.disabled = true;

  if (!config.publicKey || !form.dataset.klaviyoList) {
    if (button) button.disabled = false;
    showMessage(form, 'Subscription is temporarily unavailable — please try again shortly.', true);
    console.error('[klaviyo] missing publicKey or list id; check theme settings');
    return;
  }

  subscribe(form, email)
    .then(() => {
      form.style.display = 'none';
      // If the form has a custom success panel (e.g. the popup's discount code), reveal it.
      const panel = form.parentElement && form.parentElement.querySelector('[data-klaviyo-success-panel]');
      if (panel) {
        panel.hidden = false;
      } else {
        showMessage(form, form.dataset.klaviyoSuccess || "You're in! Check your inbox.", false);
      }
    })
    .catch((error) => {
      if (button) button.disabled = false;
      showMessage(form, 'Something went wrong — please try again.', true);
      console.error('[klaviyo]', error);
    });
}

function handleAugment(form, event) {
  if (form.dataset.klaviyoSent) return; // native submit already in flight
  event.preventDefault();

  const email = getEmail(form);
  const proceed = () => {
    form.dataset.klaviyoSent = '1';
    form.submit();
  };

  if (!email) {
    proceed();
    return;
  }

  const timeout = new Promise((resolve) => setTimeout(resolve, AUGMENT_TIMEOUT));
  Promise.race([subscribe(form, email).catch(() => {}), timeout]).then(proceed);
}

// Capture phase so we run before any other submit handler on the page.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest('form[data-klaviyo-subscribe]');
    if (!form) return;

    if (form.dataset.klaviyoMode === 'augment') {
      // Wholesale: needs Shopify's native submission to deliver the message.
      if (!config.publicKey || !form.dataset.klaviyoList) return;
      handleAugment(form, event);
    } else {
      // Newsletter / popup: always handled client-side, never submitted to Shopify.
      handleReplace(form, event);
    }
  },
  true
);

// Tap-to-copy for discount codes shown on success (e.g. the popup's FIRSTORDER code).
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-copy-code]');
  if (!trigger) return;
  const code = trigger.dataset.copyCode;
  if (navigator.clipboard) {
    navigator.clipboard.writeText(code).catch(() => {});
  }
  const label = trigger.querySelector('[data-copy-label]');
  if (label) label.textContent = 'Copied!';
});
